import { useMutation } from "@tanstack/react-query";
import axios from "axios";
import { useWebBooking } from "api/api-hooks/use-web-booking";
import { HttpStatusCode, post } from "api/api-methods";
import { verifyBookingWentThrough } from "api/verify-finalized-booking";
import { getLocaleFromLang } from "helpers/language-helper";
import { useBookingId } from "hooks/use-booking-id";
import { useBookingStateContext } from "hooks/use-booking-state";
import { useError } from "hooks/use-error";
import i18n from "i18next";
import { TrackingEventType } from "logger/event-utils";
import { error as logError, sendToParent, trackEvent } from "logger/logger";
import { pageTrackingService, TRACKING_VERSION } from "logger/page-tracking";
import queryClient from "query-client";
import { useNavigate, useParams } from "react-router-dom";
import { filterSearchParams, TRANSIENT_BOOKING_PARAMS } from "search-params/filter-search-params";
import { State } from "store/store-types";
import { CreateReservationParameters, mapCustomerForApi } from "types/request-parameters";
import { TwoFactorChallenge, TwoFactorRequiredError } from "types/two-factor";
import { WebBooking } from "types/web-booking";
import { WebRuleBookingType } from "types/web-rule";

export const useFinalizeBooking = () => {
    const { system } = useParams();
    const navigate = useNavigate();
    const { openReservationFailedModal } = useError();
    const { data: reservation } = useWebBooking();
    const bookingId = useBookingId();
    const { isActivityBooking } = useBookingStateContext();

    const finalizeBooking = async (state: State) => {
        if (!bookingId || !reservation) {
            // Returning silently here made the click vanish: no request, no error, and
            // TanStack recorded the mutation as a success, so even the spinner went away
            // (BOOK-3942). The submit button is gated on the reservation now, so reaching
            // this means something got past that gate — record it rather than swallow it.
            //
            // Deliberately no openReservationFailedModal(). That modal cannot be dismissed
            // (closeOnEsc and closeOnOverlayClick are both false) and its one button clears
            // the guest's date and times to send them back to the picker — the right answer
            // to "the slot is gone", not to "the reservation has not arrived yet". It would
            // render blank here anyway: its body comes from an errorCode this path never sets.
            const reason = !bookingId ? "no booking id" : "the reservation has not loaded";
            const abort = new Error(`Finalize aborted before sending: ${reason}.`);

            logError(abort);
            trackEvent("Booking.Finalized.Aborted", {
                sessionId: pageTrackingService.getCurrentContext().sessionId,
                reason,
                trackingVersion: TRACKING_VERSION,
            });

            throw abort;
        }

        const data: CreateReservationParameters = {
            ...state,
            lang: getLocaleFromLang(i18n.language),
            logMessage: "Bokning skapad",
            message: state.contact?.message,
            times: reservation.getBookTimes().map((time) => {
                return {
                    ruleId: time.ruleId,
                    start: time.start.toISOString(),
                };
            }),
            customer: state.customer ? mapCustomerForApi(state.customer) : undefined,
        };

        const startTime = new Date().getTime();

        /** Everything that follows from the booking being in the restaurant's hands. */
        const onFinalized = (booking: WebBooking) => {
            const searchParamsToKeep = filterSearchParams(
                window.location.search,
                TRANSIENT_BOOKING_PARAMS
            );

            const searchParamsString = searchParamsToKeep ? `?${searchParamsToKeep}` : "";

            let pathname = `/booking/${system}`;
            if (isActivityBooking) pathname += "/activity-booking";
            pathname += `/${bookingId}`;
            pathname += searchParamsString;
            queryClient.setQueryData<WebBooking>(["booking", bookingId], booking);
            // Refetch to get complete data (Finalize response does not include the webrule. Should fix this on the backend.)
            void queryClient.refetchQueries({ queryKey: ["booking", bookingId] });
            void navigate(pathname, {
                state: { isFinal: true, skipValidation: true },
            });

            // TODO: revisit the scroll event implementation. (logic brought from previous widget 3.0)
            document.documentElement.scrollTop = 0;
            document.body.scrollTop = 0;
            window.parent.postMessage("scroll2top", "*");

            const endTime = new Date().getTime();
            const loadTime = endTime - startTime;
            trackEvent("BookingFinalized", {
                guests: state.guests ?? 0,
                children: state.guestsChildren ?? 0,
                date: state.times?.[0]?.start.toISOString() ?? "",
                isWaitingList: (state.times?.length ?? 0) > 1,
                ms: loadTime,
            });

            // Track booking finalization success for conversion tracking
            const pageContext = pageTrackingService.getCurrentContext();
            const isBookingRequest = reservation.isInquiry();
            const articles = reservation.articles ?? [];
            const ruleMap = new Map((reservation.webBookingRules ?? []).map((r) => [r.id, r]));
            const experienceArticleCount = articles.filter(
                (a) => ruleMap.get(a.ruleId)?.bookingType === WebRuleBookingType.Experience
            ).length;
            const nonExperienceArticleCount = articles.length - experienceArticleCount;
            const nonTableArticles = reservation.nonTableArticles ?? [];
            const preorderAmountExVat = nonTableArticles.reduce(
                (sum, a) => sum + a.outAmountExVat,
                0
            );
            const preorderQuantity = nonTableArticles.reduce((sum, a) => sum + a.quantity, 0);
            const isActivityBookingReservation = reservation.isActivityBooking();
            trackEvent("Booking.Finalized.Success", {
                guests: state.guests ?? 0,
                children: state.guestsChildren ?? 0,
                date: state.times?.[0]?.start.toISOString() ?? "",
                isWaitingList: (state.times?.length ?? 0) > 1,
                isBookingRequest: isBookingRequest,
                sessionId: pageContext.sessionId,
                durationMs: loadTime,
                trackingVersion: TRACKING_VERSION,
                hasExperiences: experienceArticleCount > 0,
                hasNonExperiences: nonExperienceArticleCount > 0,
                preorderAmountExVat,
                preorderQuantity,
                isActivityBooking: isActivityBookingReservation,
            });

            sendToParent(state, TrackingEventType.bookingFinalized);
        };

        try {
            const booking = await post<WebBooking, CreateReservationParameters>(
                `WebBooking/WebBookings/${bookingId}/Finalize`,
                data
            );

            const finalized = new WebBooking(booking);
            onFinalized(finalized);
            return finalized;
        } catch (error) {
            // 428 means the booking is alive and waiting for an sms code, not that it failed. Has
            // to come before the recovery polling below, which would otherwise delay the code
            // prompt by several seconds and then report a failure that did not happen.
            if (
                axios.isAxiosError(error) &&
                error.response?.status === HttpStatusCode.PreconditionRequired
            ) {
                const challenge = error.response.data as TwoFactorChallenge;
                trackEvent("Booking.Finalized.TwoFactorRequired", {
                    sessionId: pageTrackingService.getCurrentContext().sessionId,
                    challengeStatus: challenge.challengeStatus ?? "",
                    verificationStatus: challenge.verificationStatus ?? "",
                    trackingVersion: TRACKING_VERSION,
                });
                throw new TwoFactorRequiredError(challenge);
            }

            // The reply goes missing on roughly 1 in 90 finalize calls, usually
            // because a mobile browser dropped the request when the page went to
            // the background. The booking itself is created and confirmed, so ask
            // the server what happened rather than sending the guest back to book
            // a second time (BOOK-3829).
            const verified = await verifyBookingWentThrough(bookingId);
            if (verified) {
                // Not logged as an error: the guest got their booking. The event
                // below carries the message so the rate stays measurable.
                const pageContext = pageTrackingService.getCurrentContext();
                trackEvent("Booking.Finalized.RecoveredLostResponse", {
                    sessionId: pageContext.sessionId,
                    durationMs: new Date().getTime() - startTime,
                    errorMessage: error instanceof Error ? error.message : "Unknown error",
                    trackingVersion: TRACKING_VERSION,
                });

                onFinalized(verified);
                return verified;
            }

            logError(error);
            openReservationFailedModal();

            const endTime = new Date().getTime();
            const loadTime = endTime - startTime;
            trackEvent("BookingFinalizeFailed", {
                guests: state.guests ?? 0,
                children: state.guestsChildren ?? 0,
                date: state.times?.[0]?.start.toISOString() ?? "",
                isWaitingList: (state.times?.length ?? 0) > 1,
                ms: loadTime,
            });

            // Track booking finalization failure for conversion tracking
            const pageContext = pageTrackingService.getCurrentContext();
            const isBookingRequest = reservation.isInquiry();
            trackEvent("Booking.Finalized.Failure", {
                guests: state.guests ?? 0,
                children: state.guestsChildren ?? 0,
                date: state.times?.[0]?.start.toISOString() ?? "",
                isWaitingList: (state.times?.length ?? 0) > 1,
                isBookingRequest: isBookingRequest,
                sessionId: pageContext.sessionId,
                durationMs: loadTime,
                errorMessage: error instanceof Error ? error.message : "Unknown error",
                trackingVersion: TRACKING_VERSION,
            });

            sendToParent(state, TrackingEventType.bookingFinalizedFailed);
            throw error;
        }
    };

    return useMutation({
        mutationKey: ["finalize-booking"],
        mutationFn: finalizeBooking,
        scope: {
            id: "webbooking-create",
        },
    });
};
